/**
 * Module-scoped ring buffer of recent API calls — fed by both the apiClient
 * (canonical path, fully-typed entries) and the window.fetch shim (backstop
 * for non-apiClient callsites, minimal entries).
 */

export interface ApiHistoryEntry {
	method: string;
	endpoint: string;
	status: number;
	durationMs: number;
	timestamp: number;
	traceId?: string;
	attempt?: number;
	error?: { code: string; message: string };
}

const DEFAULT_MAX = 20;
let buffer: ApiHistoryEntry[] = [];
let maxEntries = DEFAULT_MAX;

const PARAM_DENYLIST =
	/(token|access_token|id_token|refresh_token|code|state|nonce|api_key|client_secret|auth)=[^&#]*/gi;
const AUTH_ENDPOINT_PATTERN = /\/(auth|oauth|login|logout|refresh|token)(\/|$|\?)/i;

function redactUrl(url: string): string {
	return url.replace(PARAM_DENYLIST, (_match, paramName) => `${paramName}=[REDACTED]`);
}

function isAuthEndpoint(url: string): boolean {
	return AUTH_ENDPOINT_PATTERN.test(url);
}

export function record(entry: Omit<ApiHistoryEntry, 'timestamp'>): void {
	if (isAuthEndpoint(entry.endpoint)) return; // never store auth/token paths
	buffer.push({
		...entry,
		endpoint: redactUrl(entry.endpoint),
		timestamp: Date.now()
	});
	while (buffer.length > maxEntries) buffer.shift();
}

export const apiHistory = {
	/** Returns up to the last N entries (newest last). */
	getSnapshot(n: number = DEFAULT_MAX): ApiHistoryEntry[] {
		return buffer.slice(-n);
	},
	/** Test-only — empties the buffer. */
	clear(): void {
		buffer = [];
	},
	/** Test-only — adjust the cap (default 20). */
	setMaxEntries(n: number): void {
		maxEntries = n;
	}
};
